import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronDown, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import GlassCard from "../components/GlassCard";
import PrimaryButton from "../components/PrimaryButton";
import BottomNav from "../components/BottomNav";
import PageShell from "../components/PageShell";

const labelsByRole = {
  therapist: [
    ["session_overview", "Session Overview"],
    ["key_themes", "Key Themes"],
    ["client_progress_observations", "Client Progress and Observations"],
    ["goals_next_steps", "Goals and Next Steps"],
  ],
  coach: [
    ["session_overview", "Session Overview"],
    ["key_wins_insights", "Key Wins and Insights"],
    ["action_items", "Action Items"],
    ["focus_next_session", "Focus for Next Session"],
  ],
};

const Results = () => {
  const { sessionId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [showTranscript, setShowTranscript] = useState(false);

  useEffect(() => {
    api.get(`/sessions/${sessionId}`).then((r) => setData(r.data)).catch(() => nav("/dashboard"));
  }, [sessionId, nav]);

  if (!data) return <div className="p-8 text-center text-[#4A5D52]">Loading…</div>;

  const isAI = data.source_type === "ai_listen";
  const labels = labelsByRole[data.user_role] || labelsByRole.therapist;

  return (
    <PageShell showSettings>
      <div className="pt-16 pb-40 max-w-2xl mx-auto flex flex-col gap-6 well-page-enter">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#8BA38E]/15 border border-[#8BA38E]/30">
            <CheckCircle2 size={14} className="text-[#6A7F6C]" />
            <span className="text-xs font-medium text-[#6A7F6C]">Sent</span>
          </div>
          <h1 className="mt-3 text-3xl font-bold text-[#2A3B32]">{data.client_name}</h1>
          <p className="mt-1 text-sm text-[#8A9C91]">
            {new Date(data.sent_at || data.created_at).toLocaleString()}
          </p>
          {data.email_sent && (
            <p className="mt-2 text-xs text-[#6A7F6C]" data-testid="email-sent-indicator">
              Summary emailed to your inbox
            </p>
          )}
        </div>

        {isAI && labels.map(([key, label]) => (
          <GlassCard key={key} data-testid={`section-${key}`}>
            <h3 className="text-xs font-semibold text-[#8BA38E] uppercase tracking-wider">{label}</h3>
            <p className="mt-3 text-base text-[#2A3B32] leading-relaxed">
              {data.sections?.[key] || "—"}
            </p>
          </GlassCard>
        ))}

        {!isAI && (
          <GlassCard data-testid="written-notes-card">
            <h3 className="text-xs font-semibold text-[#8BA38E] uppercase tracking-wider">Session Notes</h3>
            <p className="mt-3 text-base text-[#2A3B32] leading-relaxed whitespace-pre-wrap">
              {data.notes_content || "—"}
            </p>
          </GlassCard>
        )}

        {(data.attached_feedback_ids || []).length > 0 && (
          <GlassCard data-testid="attached-feedback-summary">
            <h3 className="text-xs font-semibold text-[#8BA38E] uppercase tracking-wider">Attached Client Feedback</h3>
            <p className="mt-3 text-sm text-[#4A5D52]">
              {data.attached_feedback_ids.length} feedback note{data.attached_feedback_ids.length !== 1 ? "s" : ""} included with this session.
            </p>
          </GlassCard>
        )}

        {isAI && data.transcript && (
          <GlassCard>
            <button
              onClick={() => setShowTranscript((v) => !v)}
              className="w-full flex items-center justify-between"
              data-testid="toggle-transcript-btn"
            >
              <span className="font-semibold text-[#2A3B32]">Full transcript</span>
              <ChevronDown
                size={20}
                className={`text-[#4A5D52] transition-transform ${showTranscript ? "rotate-180" : ""}`}
              />
            </button>
            {showTranscript && (
              <p
                className="mt-4 text-sm text-[#4A5D52] whitespace-pre-wrap leading-relaxed"
                data-testid="transcript-content"
              >
                {data.transcript}
              </p>
            )}
          </GlassCard>
        )}

        <div className="max-w-md mx-auto w-full">
          <PrimaryButton onClick={() => nav("/new-session")} data-testid="new-session-btn">
            Start New Session
          </PrimaryButton>
        </div>
      </div>
      <BottomNav />
    </PageShell>
  );
};

export default Results;
